import { Switch } from "antd"
import React, { useState, useEffect } from "react"
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  FormFeedback,
  Row,
  Col,
} from "reactstrap"
import Swal from "sweetalert2"

const AddEditLibraryModal = ({
  isOpen,
  toggle,
  onSave,
  editData,
  libraryData,
}) => {
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    publishDate: "",
    media: null,
    ytlink: "",
    type: false, // existing "Connect with Strava" switch
    isIntro: false,
  })

  const [mediaLink, setmediaLink] = useState("")
  const [errors, setErrors] = useState({})
  const [useYouTube, setUseYouTube] = useState(false) // NEW toggle state for input mode

  // Prefill form when editing
  useEffect(() => {
    if (editData) {
      setFormData({
        title: editData.title || "",
        description: editData.description || "",
        publishDate: editData.publishDate || "",
        media: null,
        ytlink: editData.ytlink || "",
        type: editData?.type || false,
        isIntro: editData?.isIntro || false,
      })
      setmediaLink(editData?.media)
      setUseYouTube(!!editData?.ytlink) // if editing item has ytlink, switch to YouTube mode
    } else {
      setFormData({
        title: "",
        description: "",
        publishDate: "",
        media: null,
        ytlink: "",
        type: false,
        isIntro: false,
      })
      setmediaLink("")
      setUseYouTube(false)
    }
  }, [editData, isOpen])

  const validate = () => {
    let newErrors = {}

    if (!formData.title) newErrors.title = "Title is required"
    if (!formData.description) newErrors.description = "Description is required"
    if (!formData.type && !formData.publishDate)
      newErrors.publishDate = "Publish Date is required"

    if (useYouTube) {
      if (!formData.ytlink)
        newErrors.ytlink = "YouTube link is required in this mode"
      else if (
        !/^https?:\/\/(www\.)?(youtube\.com|youtu\.be)/.test(formData.ytlink)
      )
        newErrors.ytlink = "Enter a valid YouTube link"
    } else {
      if (!formData.media && !mediaLink)
        newErrors.media = "Upload an image or video file"
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleChange = e => {
    const { name, value, files } = e.target
    setErrors(prev => ({ ...prev, [name]: "" }))

    if (name === "media") {
      const selectedFile = files?.[0]
      if (!selectedFile) return

      const fileName = selectedFile.name.toLowerCase()
      const fileType = selectedFile.type
      const allowedTypes = ["image/", "video/"]
      const isAllowedType = allowedTypes.some(type => fileType.startsWith(type))

      if (!isAllowedType) {
        setErrors(prev => ({
          ...prev,
          media: "Only image or video files are allowed.",
        }))
        setFormData({ ...formData, media: null })
        return
      }

      if (fileName.endsWith(".mov")) {
        Swal.fire({
          text: ".mov files are not supported. Please upload .mp4, .webm, or supported formats.",
          icon: "error",
        })
        setFormData({ ...formData, media: null })
        return
      }

      setFormData({ ...formData, media: selectedFile })
    } else {
      setFormData({ ...formData, [name]: value })
    }
  }

  const handleSave = () => {
    if (!validate()) return

    const newEntry = {
      title: formData.title,
      description: formData.description,
      publishDate: formData.publishDate,
      // Exclude useYouTube toggle — not part of API
      media: useYouTube ? null : formData.media,
      ytlink: useYouTube ? formData.ytlink : "",
      type: formData.type,
      isIntro: formData?.isIntro,
    }

    onSave(newEntry, !!editData, editData?._id)
    setFormData({
      title: "",
      description: "",
      publishDate: "",
      media: null,
      ytlink: "",
      type: false,
    })
    setErrors({})
    toggle()
  }

  return (
    <Modal isOpen={isOpen} toggle={toggle} centered size="lg">
      <ModalHeader toggle={toggle}>
        {editData ? "Edit Library Item" : "Add New Library Item"}
      </ModalHeader>
      <ModalBody>
        <Form>
          {/* Title */}
          <FormGroup>
            <Label>
              Title <span className="text-danger">*</span>
            </Label>
            <Input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              invalid={!!errors.title}
              placeholder="Enter title"
            />
            {errors.title && <FormFeedback>{errors.title}</FormFeedback>}
          </FormGroup>

          {/* Description */}
          <FormGroup>
            <Label>
              Description <span className="text-danger">*</span>
            </Label>
            <Input
              type="textarea"
              name="description"
              value={formData.description}
              onChange={handleChange}
              invalid={!!errors.description}
              placeholder="Enter description"
            />
            {errors.description && (
              <FormFeedback>{errors.description}</FormFeedback>
            )}
          </FormGroup>

          {/* Toggle between YouTube and File Upload */}
          <Row className="my-3">
            <Col md={4}>
              <FormGroup>
                <Label>Choose Media Type</Label>
                <div className="d-flex align-items-center gap-2">
                  <Switch
                    checked={useYouTube}
                    onChange={checked => {
                      setUseYouTube(checked)
                      setErrors({})
                      setFormData(prev => ({
                        ...prev,
                        ytlink: "",
                        media: null,
                      }))
                    }}
                  />
                  <span>{useYouTube ? "YouTube Link" : "Upload Media"}</span>
                </div>
              </FormGroup>
            </Col>
            <Col md={4}>
              {/* Conditionally render based on toggle */}
              {useYouTube ? (
                <FormGroup>
                  <Label>
                    YouTube Link <span className="text-danger">*</span>
                  </Label>
                  <Input
                    type="text"
                    name="ytlink"
                    value={formData.ytlink}
                    onChange={handleChange}
                    invalid={!!errors.ytlink}
                    placeholder="Enter YouTube Link"
                  />
                  {errors.ytlink && (
                    <FormFeedback>{errors.ytlink}</FormFeedback>
                  )}
                </FormGroup>
              ) : (
                <FormGroup>
                  <Label>
                    Upload Image/Video <span className="text-danger">*</span>
                  </Label>
                  <Input
                    type="file"
                    name="media"
                    accept="image/*,video/*"
                    onChange={handleChange}
                    invalid={!!errors.media}
                  />
                  {errors.media && <FormFeedback>{errors.media}</FormFeedback>}
                  {mediaLink && (
                    <p className="my-1">
                      <b>Uploaded File:</b> {mediaLink?.split("/")?.[1]}
                    </p>
                  )}
                </FormGroup>
              )}
            </Col>
            {!formData.type && (
              <FormGroup className="col-xl-4">
                <Label>
                  Publish Date <span className="text-danger">*</span>
                </Label>
                <Input
                  type="date"
                  name="publishDate"
                  value={formData.publishDate}
                  onChange={handleChange}
                  invalid={!!errors.publishDate}
                />
                {errors.publishDate && (
                  <FormFeedback>{errors.publishDate}</FormFeedback>
                )}
              </FormGroup>
            )}
          </Row>

          {/* Strava & Date Row */}
          <Row>
            <FormGroup className="col-xl-6 d-flex flex-column">
              <Label>Connect With Strava Account Connect Video</Label>
              <Switch
                style={{ width: "10%" }}
                checked={formData.type}
                onChange={status => {
                  if (status) {
                    const assigned = libraryData.filter(i => i.type)
                    if (
                      formData?.title !== assigned?.[0]?.title &&
                      assigned.length > 0
                    ) {
                      Swal.fire({
                        title: "Already Assigned",
                        text: "You have already assigned another video to the Strava Account Page. Please remove that first.",
                        icon: "warning",
                      })
                    } else {
                      setFormData({ ...formData, type: status })
                    }
                  } else {
                    setFormData({ ...formData, type: status })
                  }
                }}
              />
            </FormGroup>
            <FormGroup className="col-xl-6 d-flex flex-column">
              <Label>Connect With Introduction Video</Label>
              <Switch
                style={{ width: "10%" }}
                checked={formData.isIntro}
                onChange={status => {
                  if (status) {
                    const assigned = libraryData.filter(i => i.isIntro)
                    if (
                      formData?.title !== assigned?.[0]?.title &&
                      assigned.length > 0
                    ) {
                      Swal.fire({
                        title: "Already Assigned",
                        text: "You have already assigned another video to the Strava Account Page. Please remove that first.",
                        icon: "warning",
                      })
                    } else {
                      setFormData({ ...formData, isIntro: status })
                    }
                  } else {
                    setFormData({ ...formData, isIntro: status })
                  }
                }}
              />
            </FormGroup>
          </Row>
        </Form>
      </ModalBody>

      <ModalFooter>
        <Button color="primary" onClick={handleSave}>
          {editData ? "Update" : "Save"}
        </Button>
        <Button color="secondary" onClick={toggle}>
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  )
}

export default AddEditLibraryModal
